/**
 * Post-generation tasks: auto db:push + lint:fix
 */

import { execFileSync } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'
import { getAllTrackedFilePaths } from '@core-engine/snapshots/store.js'
import type { PackageManager } from '@core-engine/utils/package-manager.js'
import { detectPackageManager } from '@core-engine/utils/package-manager.js'
import {
  DRIZZLE_KIT_POSTGRES_DRIVER_DEP,
  hasDrizzleKitPostgresDriverDependency,
  hasPostgresRuntimeDependency,
  POSTGRES_RUNTIME_DEP
} from '@next-adapter/init/scaffolders/dependencies.js'
import { runDrizzlePush } from '@next-adapter/utils/drizzle-push.js'

const BIOME_FORMAT_TIMEOUT_MS = 30_000

/**
 * Load .env.local and set variables on process.env (if not already set)
 */
function loadEnvFile(cwd: string): void {
  const envPath = path.join(cwd, '.env.local')
  if (!fs.existsSync(envPath)) return

  const content = fs.readFileSync(envPath, 'utf-8')
  for (const line of content.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) continue

    const eqIdx = trimmed.indexOf('=')
    if (eqIdx === -1) continue

    const key = trimmed.slice(0, eqIdx).trim()
    let value = trimmed.slice(eqIdx + 1).trim()

    // Strip surrounding quotes
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1)
    }

    // Strip inline comments
    const commentIdx = value.indexOf('  #')
    if (commentIdx !== -1) {
      value = value.slice(0, commentIdx).trim()
    }

    if (!process.env[key]) {
      process.env[key] = value
    }
  }
}

function runPmScript(pm: PackageManager, script: string, cwd: string, timeout?: number): boolean {
  const args = pm === 'bun' ? ['run', script] : [script]
  try {
    execFileSync(pm, args, { cwd, stdio: 'pipe', timeout })
    return true
  } catch {
    return false
  }
}

function getExistingTrackedFilePaths(cwd: string): string[] {
  return getAllTrackedFilePaths(cwd).filter((filePath) =>
    fs.existsSync(path.join(cwd, ...filePath.split('/')))
  )
}

function runBiomeCheckWrite(cwd: string): boolean {
  const biomeBin = path.join(cwd, 'node_modules', '.bin', 'biome')
  if (!fs.existsSync(biomeBin)) {
    return false
  }

  const filePaths = getExistingTrackedFilePaths(cwd)
  if (filePaths.length === 0) {
    return true
  }

  const chunkSize = 80
  const configPath = fs.existsSync(path.join(cwd, 'biome.json')) ? 'biome.json' : null
  for (let index = 0; index < filePaths.length; index += chunkSize) {
    const chunk = filePaths.slice(index, index + chunkSize)
    const args = [
      'check',
      '--write',
      ...(configPath ? ['--config-path', configPath] : []),
      ...chunk
    ]
    execFileSync(biomeBin, args, {
      cwd,
      stdio: 'pipe',
      timeout: BIOME_FORMAT_TIMEOUT_MS
    })
  }

  return true
}

function buildAddDependencyArgs(pm: PackageManager, dependency: string, dev: boolean): string[] {
  const devFlag = dev ? (pm === 'yarn' ? '--dev' : '-D') : ''

  switch (pm) {
    case 'pnpm':
    case 'yarn':
    case 'bun':
      return ['add', ...(devFlag ? [devFlag] : []), dependency]
    default:
      return ['install', ...(devFlag ? [devFlag] : []), dependency]
  }
}

function installDependency(
  cwd: string,
  pm: PackageManager,
  dependency: string,
  dev = false
): boolean {
  try {
    execFileSync(pm, buildAddDependencyArgs(pm, dependency, dev), {
      cwd,
      stdio: 'pipe'
    })
    return true
  } catch {
    return false
  }
}

function ensureDatabasePushDependencies(cwd: string, pm: PackageManager): boolean {
  const missing: Array<{ name: string; dev: boolean }> = []

  if (!hasPostgresRuntimeDependency(cwd)) {
    missing.push({ name: POSTGRES_RUNTIME_DEP, dev: false })
  }

  if (!hasDrizzleKitPostgresDriverDependency(cwd)) {
    missing.push({ name: DRIZZLE_KIT_POSTGRES_DRIVER_DEP, dev: true })
  }

  if (missing.length === 0) {
    return true
  }

  console.log(
    `\n  Installing database push dependencies (${missing.map((dependency) => dependency.name).join(', ')})...`
  )

  for (const dependency of missing) {
    const installed = installDependency(cwd, pm, dependency.name, dependency.dev)
    if (!installed) {
      console.log(`  Failed to install ${dependency.name}`)
      return false
    }
  }

  const ready = hasPostgresRuntimeDependency(cwd) && hasDrizzleKitPostgresDriverDependency(cwd)

  if (ready) {
    console.log('  Database push dependencies installed')
  } else {
    const unresolved = [
      !hasPostgresRuntimeDependency(cwd) ? POSTGRES_RUNTIME_DEP : null,
      !hasDrizzleKitPostgresDriverDependency(cwd) ? DRIZZLE_KIT_POSTGRES_DRIVER_DEP : null
    ].filter((dependency): dependency is string => Boolean(dependency))
    console.log(`  Installed dependencies but could not resolve ${unresolved.join(', ')}`)
  }

  return ready
}

function hasPkgScript(cwd: string, script: string): boolean {
  const pkgPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(pkgPath)) return false
  try {
    const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8')) as Record<string, unknown>
    const scripts = pkg.scripts as Record<string, unknown> | undefined
    return !!scripts?.[script]
  } catch {
    return false
  }
}

export interface PostGenerateOptions {
  skipMigration?: boolean
  skipMigrationMessage?: string
  hasConflicts?: boolean
  conflictPaths?: string[]
  showNextSteps?: boolean
  adminRoutePath?: string
  schemaRoutePath?: string
}

export interface PostGenerateResult {
  dbPush: 'success' | 'skipped' | 'no-db-url' | 'failed'
  lintFix: 'success' | 'skipped' | 'failed'
}

export function scanConflictPaths(cwd: string): string[] {
  const conflictPaths: string[] = []

  for (const filePath of getAllTrackedFilePaths(cwd)) {
    const fullPath = path.join(cwd, ...filePath.split('/'))
    if (!fs.existsSync(fullPath)) {
      continue
    }

    const content = fs.readFileSync(fullPath, 'utf-8')
    if (/^(<{7}|={7}|>{7})( |$)/m.test(content)) {
      conflictPaths.push(filePath)
    }
  }

  return conflictPaths.sort()
}

/**
 * Run post-generation tasks: db:push and lint:fix
 */
export async function runPostGenerate(
  cwd: string,
  schemaName: string,
  options: PostGenerateOptions = {}
): Promise<PostGenerateResult> {
  const pm = detectPackageManager(cwd)
  const result: PostGenerateResult = {
    dbPush: 'skipped',
    lintFix: 'skipped'
  }

  if (options.hasConflicts) {
    result.dbPush = 'skipped'
    result.lintFix = 'skipped'

    console.log(
      `\n  Database: skipped (resolve conflict markers in ${options.conflictPaths?.length ?? 0} file(s), then re-run)`
    )
    console.log('  Formatting: skipped (biome refuses files with conflict markers)')
    for (const conflictPath of options.conflictPaths ?? []) {
      console.log(`    - ${conflictPath}`)
    }
    console.log(
      '\n  Resolve markers in the files above and re-run the BetterStart command that wrote them. Post-write tasks run automatically once every file is clean.'
    )

    return result
  }

  // 1. Database push
  if (!options.skipMigration) {
    loadEnvFile(cwd)

    const dbUrl = process.env.DATABASE_URL

    if (!dbUrl) {
      result.dbPush = 'no-db-url'
      console.log('\n  Database: skipped (no DATABASE_URL configured)')
      console.log('    To sync later: run db:push after setting DATABASE_URL')
    } else if (!ensureDatabasePushDependencies(cwd, pm)) {
      result.dbPush = 'failed'
      console.log(
        '  Database push failed (install database dependencies and run drizzle-kit push manually)'
      )
    } else if (hasPkgScript(cwd, 'db:push')) {
      console.log('\n  Running db:push...')
      const ok = runPmScript(pm, 'db:push', cwd)
      result.dbPush = ok ? 'success' : 'failed'
      console.log(ok ? '  Database schema synced' : '  Database push failed (run db:push manually)')
    } else {
      // Try running drizzle-kit push directly using project-local binary
      console.log('\n  Running drizzle-kit push...')
      try {
        const pushResult = await runDrizzlePush(cwd, { inheritStdio: true })
        if (!pushResult.success) {
          throw new Error(pushResult.error ?? 'drizzle-kit push failed')
        }
        result.dbPush = 'success'
        console.log('  Database schema synced')
      } catch {
        result.dbPush = 'failed'
        console.log('  Database push failed (run drizzle-kit push manually)')
      }
    }
  } else {
    console.log(`\n  Database: skipped (${options.skipMigrationMessage ?? '--skip-migration'})`)
  }

  // 2. Lint fix
  if (hasPkgScript(cwd, 'lint:fix')) {
    console.log('  Running lint:fix...')
    const ok = runPmScript(pm, 'lint:fix', cwd, BIOME_FORMAT_TIMEOUT_MS)
    result.lintFix = ok ? 'success' : 'failed'
    console.log(ok ? '  Code formatted' : '  Lint fix had issues (run lint:fix manually)')
  } else {
    // Try biome directly using project-local binary
    try {
      if (!runBiomeCheckWrite(cwd)) {
        throw new Error('Biome binary not found')
      }
      result.lintFix = 'success'
      console.log('  Code formatted with Biome')
    } catch {
      result.lintFix = 'failed'
      console.log('  Biome formatting had issues (run biome check --write manually)')
    }
  }

  if (options.showNextSteps !== false) {
    const schemaRoute =
      options.schemaRoutePath ?? `${options.adminRoutePath ?? '/admin'}/${schemaName}`
    console.log('\n  Next steps:')
    console.log('    1. Review the generated files')
    if (options.skipMigration || result.dbPush !== 'success') {
      console.log('    2. Run database migration: db:push')
      console.log(`    3. Start the dev server and visit ${schemaRoute}`)
    } else {
      console.log(`    2. Start the dev server and visit ${schemaRoute}`)
    }
  }

  return result
}
